/**
 * Shows the published release notes in the Produktnyheter view of the round "?" bubble
 * at the bottom left — Pendo's Resource Center, not Elevio.
 *
 * Pendo's own announcements are authored in their dashboard and cannot be repointed
 * from here, but the Resource Center renders into our DOM: their `<ol>` is the panel's
 * scroll container, so the releases go in as its first row and the two read as one
 * list. Everything here is keyed off structure rather than their hashed class names,
 * and if it stops matching nothing is inserted and their list shows as it does today.
 */
import config from 'App/babel/config';
import T, { getCurrentLanguage } from 'Components/Helpers/translate';
import logError from 'Helpers/logError';

const CONTAINER_ID = 'pendo-resource-center-container';
const FRAME_ID = 'up-product-news-frame';
const HEIGHT_MESSAGE = 'upsales-product-news-height';
const MAX_HEIGHT = 4000;
/** After the page has loaded, how long its height may still be on the way. */
const SETTLE_MS = 2000;
/** How long a page that never loads at all is given. */
const LOAD_MS = 20000;

/**
 * The launcher, as Pendo's own agent addresses it when it sets `aria-expanded` — the
 * underscore prefix is theirs. Their unread bubble lives in this same element, so it is
 * also where a dot belongs.
 */
const LAUNCHER = 'button._pendo-resource-center-badge-container, ._pendo-resource-center-badge-container';
/** What Pendo hangs on each module row of the home view, holding that module's guide id. */
const MODULE_ATTR = 'data-pendo-module-guide-id';
const LAUNCHER_DOT_ID = 'up-product-news-dot';
const ROW_DOT_ID = 'up-product-news-row-dot';
/**
 * Produktnyheter's module, as Pendo identifies it. Read off the live Resource Center
 * because it is account data rather than anything this repo owns, and the same in every
 * environment: the Pendo api key is hardcoded in `ui/index.html`, so one subscription —
 * and one set of module ids — serves them all.
 *
 * Named rather than positional or matched on its label: the rows can be reordered in
 * Pendo's dashboard, and the label is translated.
 */
const MODULE_GUIDE_ID = 'AbmilcyKNOQ8Ie0fdV4tRVj_lLI@qDqjxU4kk6wkgMWb5LmbirjW9wk';
/**
 * Where the module id is remembered when the one above stops matching — rebuild the
 * module in Pendo and it takes a new guide id, which would otherwise leave the row
 * unbadged for good. Opening the module identifies it: the announcements view we insert
 * into IS that module, so the row clicked immediately beforehand is the one.
 */
const MODULE_KEY = 'upsales:productNews:moduleGuideId';

/**
 * Whether this customer gets the panel at all: whether there is anywhere to point at.
 * The configured page is empty until an environment has a published release-notes page,
 * and blank in every committed config.
 */
const isEnabled = () => Boolean(config.PRODUCT_NEWS_URL);

let pageOrigin: string | null = null;
let observer: MutationObserver | null = null;
let frame: HTMLIFrameElement | null = null;
let row: HTMLLIElement | null = null;
let sized = false;
let unavailable = false;
let driftLogged = false;
let unreadCount = 0;
let markSeen: (() => void) | null = null;
let lastModuleOpened: string | null = null;

const originOf = (url: string) => {
	try {
		return new URL(url, window.location.href).origin;
	} catch (e) {
		logError(e, 'Product news: PRODUCT_NEWS_URL is not a valid url', config.PRODUCT_NEWS_URL);
		return null;
	}
};

/**
 * `compact=1` asks the page for its digest — a headline and intro per release, each
 * linking to the full page — rather than the reader, which does not fit a panel this
 * narrow. The language travels in the url because the page only appends `?lang=` for
 * its own modules.
 */
const frameUrl = () => {
	const lang = getCurrentLanguage()?.toLowerCase().startsWith('sv') ? 'sv' : 'en';
	const separator = config.PRODUCT_NEWS_URL.includes('?') ? '&' : '?';
	return `${config.PRODUCT_NEWS_URL}${separator}compact=1&lang=${lang}`;
};

/** Nothing to show: leave the panel as Pendo's own list and stop looking. */
const giveUp = (reason: string) => {
	unavailable = true;
	observer?.disconnect();
	observer = null;
	window.removeEventListener('message', onHeight);
	row?.remove();
	row = null;
	frame = null;
	logError(new Error(`Product news is unavailable: ${reason}`), 'Product news', config.PRODUCT_NEWS_URL);
};

function onHeight(event: MessageEvent) {
	if (event.origin !== pageOrigin || event.source !== frame?.contentWindow) {
		return;
	}
	const data = event.data as { type?: string; height?: unknown };
	const height = Number(data?.height);
	if (data?.type !== HEIGHT_MESSAGE || !(height > 0)) {
		return;
	}
	frame.style.height = `${Math.min(Math.round(height), MAX_HEIGHT)}px`;
	sized = true;
}

/**
 * Their markup moved: a sub-view listing rows, none of which is a guide. Logged once,
 * because otherwise this feature dies silently and nobody finds out.
 */
const reportDrift = (container: Element) => {
	if (driftLogged || !container.querySelector('ol li') || container.querySelector('ol li [id^="pendo-g-"]')) {
		return;
	}
	driftLogged = true;
	logError(new Error('Product news: the Pendo announcements list no longer matches'), 'Product news');
};

/**
 * The bubble, carrying how many releases are unread. Drawn rather than borrowed: Pendo's
 * own bubble holds a count it derives from its own unseen guides, so anything written
 * into it is overwritten the next time they recount.
 *
 * Shaped like theirs — a pill that stays circular at one digit and grows for two — and
 * labelled rather than hidden from assistive tech, because the number is the message.
 */
const bubble = (id: string, count: number, css: string) => {
	const el = document.createElement('span');
	el.id = id;
	el.textContent = count > 9 ? '9+' : String(count);
	el.setAttribute('role', 'status');
	el.setAttribute('aria-label', T('productNews.unread', { count: String(count) }));
	el.style.cssText =
		'min-width:18px;height:18px;padding:0 5px;border-radius:9px;background:#c62d2d;color:#fff;' +
		'font-size:11px;line-height:18px;font-weight:700;text-align:center;box-sizing:border-box;' +
		`font-family:inherit;${css}`;
	return el;
};

const readModuleGuideId = () => {
	try {
		return window.localStorage.getItem(MODULE_KEY);
	} catch {
		// Private mode, or storage full. The launcher dot does not depend on this.
		return null;
	}
};

const rememberModuleGuideId = (id: string) => {
	try {
		window.localStorage.setItem(MODULE_KEY, id);
	} catch {
		// Nothing to do about it; the row dot simply stays absent — and a dot that cannot
		// be placed is not worth failing the panel over.
	}
};

/**
 * Put one badge where it belongs, or take it away. Rebuilt rather than mutated when the
 * count moves — a bubble showing a stale number is worse than none.
 */
const place = (id: string, host: Element | null | undefined, css: string) => {
	const existing = document.getElementById(id);
	if (!host || !unreadCount) {
		existing?.remove();
		return;
	}
	if (existing) {
		if (
			existing.parentElement === host &&
			existing.textContent === (unreadCount > 9 ? '9+' : String(unreadCount))
		) {
			return;
		}
		existing.remove();
	}
	host.appendChild(bubble(id, unreadCount, css));
};

/**
 * The Produktnyheter row.
 *
 * Searched across the whole panel rather than under a named list: Pendo's own code
 * addresses these rows with a bare `[data-pendo-module-guide-id]` query and never scopes
 * it either, and the list around them is not reliably the id their bundle suggests.
 *
 * Matched by reading the attribute rather than by building a selector out of a stored
 * value — nothing here has to be escaped, and it needs no `CSS.escape`.
 */
const findModuleRow = () => {
	const container = document.getElementById(CONTAINER_ID) ?? document;
	const rows = Array.from(container.querySelectorAll(`[${MODULE_ATTR}]`));
	// The constant first, the learned id second: a rebuilt module leaves the constant
	// matching nothing, and then whatever the reader last opened is the better answer.
	for (const wanted of [MODULE_GUIDE_ID, readModuleGuideId()]) {
		const match = wanted && rows.find(el => el.getAttribute(MODULE_ATTR) === wanted);
		if (match) {
			return match;
		}
	}
	return null;
};

/** Both badges follow `unreadCount`, so one function puts them right whatever changed. */
const renderBadges = () => {
	// Their launcher is the positioning context for their own bubble, so it is for this
	// too; inset rather than overhanging, which theirs does.
	place(
		LAUNCHER_DOT_ID,
		document.querySelector(LAUNCHER),
		'position:absolute;top:-2px;right:-2px;box-shadow:0 0 0 2px #fff'
	);
	// Left of their caret, vertically centred on the row.
	place(ROW_DOT_ID, findModuleRow(), 'position:absolute;top:50%;right:34px;margin-top:-9px');
};

/**
 * Which module row the user last clicked, so that opening the announcements view can
 * name it. Capture phase, because Pendo replaces the home view with the sub-view on the
 * same click.
 */
const onModuleClick = (event: Event) => {
	const target = event.target as Element | null;
	const module = target?.closest?.(`[${MODULE_ATTR}]`);
	lastModuleOpened = module?.getAttribute(MODULE_ATTR) ?? lastModuleOpened;
};

const watchModuleClicks = () => {
	document.addEventListener('click', onModuleClick, true);
};

const insert = () => {
	const container = document.getElementById(CONTAINER_ID);
	if (unavailable || !container || document.getElementById(FRAME_ID)) {
		return;
	}
	const list = container.querySelector('ol li [id^="pendo-g-"]')?.closest('ol');
	if (!list) {
		reportDrift(container);
		return;
	}

	// Reaching here means the announcements view is open, which is the one module our
	// releases live in — so the row that opened it is Produktnyheter, and the user is
	// now looking at the news.
	if (lastModuleOpened) {
		rememberModuleGuideId(lastModuleOpened);
	}
	if (unreadCount) {
		unreadCount = 0;
		markSeen?.();
		renderBadges();
	}

	sized = false;
	frame = document.createElement('iframe');
	frame.id = FRAME_ID;
	frame.title = T('productNews.title');
	frame.src = frameUrl();
	// Sized to its content, with the list doing the scrolling — so it must not scroll
	// itself, or the reader gets a scrollbar inside a scrollbar.
	frame.setAttribute('scrolling', 'no');
	frame.setAttribute('referrerpolicy', 'no-referrer');
	// The pages host also serves customer-authored html, so deny it everything it does
	// not need — top-level navigation above all. `allow-same-origin` keeps the page's
	// OWN origin (it is not ours), without which the height below arrives as "null"
	// and is rejected.
	frame.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox');
	frame.style.cssText = 'width:100%;height:120px;border:0;display:block;background:#fff';

	// Pendo's rows carry their own gutter, so copy it rather than restate it; the page
	// inside renders edge to edge and lines up with the rows below.
	row = document.createElement('li');
	const sibling = list.querySelector('li');
	if (sibling) {
		const { paddingLeft, paddingRight } = window.getComputedStyle(sibling);
		row.style.padding = `0 ${paddingRight} 0 ${paddingLeft}`;
	}
	row.style.listStyle = 'none';
	row.appendChild(frame);
	list.prepend(row);

	// A page that loads and still reports no height has nothing to show. The checks are
	// tied to the load, so a slow page is waited for rather than written off, and both
	// ignore a frame Pendo has already replaced — that frame's failure is not this one's.
	const current = frame;
	current.addEventListener('load', () => {
		window.setTimeout(() => {
			if (current.isConnected && !sized) {
				giveUp('the page reported no height');
			}
		}, SETTLE_MS);
	});
	window.setTimeout(() => {
		if (current.isConnected && !sized) {
			giveUp('the page did not load');
		}
	}, LOAD_MS);
};

/** Idempotent: safe to call more than once. No-op until a page is configured. */
export const installProductNews = () => {
	if (observer || unavailable || !isEnabled()) {
		return;
	}
	pageOrigin = originOf(config.PRODUCT_NEWS_URL);
	if (!pageOrigin) {
		return;
	}
	window.addEventListener('message', onHeight);
	watchModuleClicks();
	// The panel is built and torn down on every open, so the whole body is watched.
	// Coalesced to a frame: Pendo's open animation produces mutations in bursts.
	let queued = false;
	observer = new MutationObserver(() => {
		if (queued) {
			return;
		}
		queued = true;
		window.requestAnimationFrame(() => {
			queued = false;
			insert();
			// The launcher arrives long after this runs, and the home view is rebuilt on
			// every open — so the dots are put right on the same mutations as the frame.
			renderBadges();
		});
	});
	observer.observe(document.body, { childList: true, subtree: true });
	insert();
	renderBadges();
};

/**
 * The counterpart to install: drops the observer, the listener and both dots.
 *
 * The app never tears this down — it lives as long as the session does. Tests need it,
 * because an observer left watching `document.body` outlives the module instance that
 * created it and then races the next one.
 */
export const uninstallProductNews = () => {
	observer?.disconnect();
	observer = null;
	window.removeEventListener('message', onHeight);
	document.removeEventListener('click', onModuleClick, true);
	document.getElementById(LAUNCHER_DOT_ID)?.remove();
	document.getElementById(ROW_DOT_ID)?.remove();
	pageOrigin = null;
	frame = null;
	row = null;
	sized = false;
	unavailable = false;
	driftLogged = false;
	unreadCount = 0;
	markSeen = null;
	lastModuleOpened = null;
};

/**
 * Whether there is a release this user has not seen, which is only knowable once the app
 * has its data — so it arrives separately from the setup above. `onSeen` is called once,
 * when they open Produktnyheter.
 *
 * Comparing ids, not dates: a new release always takes a higher id, while re-publishing
 * an old one keeps its original date.
 */
export const setProductNewsUnread = ({
	publishedIds,
	lastSeenId,
	onSeen
}: {
	publishedIds: number[];
	lastSeenId: number;
	onSeen: (seenId: number) => void;
}) => {
	if (!isEnabled() || !publishedIds?.length) {
		return;
	}
	unreadCount = publishedIds.filter(id => id > lastSeenId).length;
	// The marker is the highest published id, not the highest unread one — they are the
	// same number here, but reading it off the whole set keeps it right if a release is
	// ever published with an id below one already seen.
	const highest = Math.max(...publishedIds, lastSeenId);
	markSeen = () => {
		markSeen = null;
		onSeen(highest);
	};
	renderBadges();
};
